import { mapCssAssetUrls } from "./html-css-assets";

export const HTML_ASSETS_READY = "denker:private-assets-ready";
export const HTML_ASSETS_UPDATE = "denker:private-assets-update";

/** Runs inside an opaque-origin sandbox. Only the parent can supply URLs. */
export function privateHtmlAssetRuntime(
  token: string,
  mapCss: typeof mapCssAssetUrls,
): void {
  const originals = new WeakMap<Element, Map<string, string>>();
  const applied = new WeakMap<Element, Map<string, string>>();
  let mapping: Record<string, string> = {};
  let reverse: Record<string, string> = {};
  let channel: MessagePort | undefined;
  const selector = "img,source,video,audio,a,[style],style";
  const space = /[\t\n\f\r ]/;
  const mapSrcset = (
    value: string,
    resolve: (url: string) => string,
  ): string => {
    let index = 0;
    let copied = 0;
    let result = "";
    while (index < value.length) {
      while (
        index < value.length &&
        (space.test(value[index]!) || value[index] === ",")
      )
        index++;
      const start = index;
      while (index < value.length && !space.test(value[index]!)) index++;
      let end = index;
      while (end > start && value[end - 1] === ",") end--;
      const source = value.slice(start, end);
      const resolved = resolve(source);
      if (resolved && resolved !== source) {
        result += value.slice(copied, start) + resolved;
        copied = end;
      }
      if (end < index) continue;
      let parentheses = 0;
      while (index < value.length) {
        const character = value[index++];
        if (character === "(") parentheses++;
        if (character === ")") parentheses = Math.max(0, parentheses - 1);
        if (character === "," && parentheses === 0) break;
      }
    }
    return result + value.slice(copied);
  };
  const apply = () => {
    for (const element of document.querySelectorAll(selector)) {
      let attributes = originals.get(element);
      if (!attributes) {
        attributes = new Map();
        originals.set(element, attributes);
      }
      let last = applied.get(element);
      if (!last) {
        last = new Map();
        applied.set(element, last);
      }
      const names =
        element.tagName === "STYLE"
          ? ["textContent"]
          : element.tagName === "A"
            ? ["href", "style"]
            : ["src", "srcset", "poster", "style"];
      for (const name of names) {
        const current =
          name === "textContent"
            ? element.textContent
            : element.getAttribute(name);
        if (current === null) continue;
        if (
          !attributes.has(name) ||
          (last.has(name) && current !== last.get(name))
        )
          attributes.set(
            name,
            name === "style" || name === "textContent"
              ? mapCss(current, (url) => reverse[url] ?? url)
              : name === "srcset"
                ? mapSrcset(current, (url) => reverse[url] ?? url)
                : (reverse[current] ?? current),
          );
        const source = attributes.get(name)!;
        const value =
          name === "style" || name === "textContent"
            ? mapCss(source, (url) => mapping[url] ?? url)
            : name === "srcset"
              ? mapSrcset(source, (url) => mapping[url] ?? url)
              : (mapping[source] ?? source);
        if (value !== current) {
          if (name === "textContent") element.textContent = value;
          else element.setAttribute(name, value);
        }
        last.set(name, value);
      }
    }
  };
  window.addEventListener("message", (event: MessageEvent) => {
    if (
      event.source !== window.parent ||
      event.data?.type !== "denker:private-assets-request" ||
      !event.ports[0]
    )
      return;
    channel?.close();
    channel = event.ports[0];
    channel.onmessage = (message: MessageEvent) => {
      if (message.data?.type !== "denker:private-assets-update") return;
      const urls: unknown = message.data.urls;
      if (!urls || typeof urls !== "object" || Array.isArray(urls)) return;
      const next = {
        ...mapping,
        ...Object.fromEntries(
          Object.entries(urls).filter(([, value]) => typeof value === "string"),
        ),
      };
      reverse = Object.fromEntries(
        [...Object.entries(mapping), ...Object.entries(next)].map(
          ([source, resolved]) => [resolved, source],
        ),
      );
      mapping = next;
      apply();
    };
    channel.start();
    channel.postMessage({ type: "denker:private-assets-ready", token });
  });
  const ready = () => {
    new MutationObserver(apply).observe(document.documentElement, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["src", "srcset", "poster", "href", "style"],
    });
    window.parent.postMessage({ type: "denker:private-assets-listening" }, "*");
  };
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", ready, { once: true });
  else ready();
}

export function privateHtmlAssetScript(token: string): string {
  const argument = JSON.stringify(token).replace(/</g, "\\u003c");
  return `<script data-denker-runtime="private-assets">(${privateHtmlAssetRuntime.toString()})(${argument},(${mapCssAssetUrls.toString()}));</script>`;
}
